import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Cookie, X } from 'lucide-react';
import { Button } from './ui/button';

const STORAGE_KEY = 'triflow_cookie_consent_v1';

// Authenticated routes where the banner would overlap interactive UI. We hide
// it there — if a logged-in user reaches the app it implies they have already
// agreed to cookies via the login/register flow (session cookie is essential).
const AUTHENTICATED_PATHS = [
  '/dashboard', '/calendar', '/log', '/stats', '/plans', '/profile',
  '/subscription', '/connections', '/library', '/admin', '/onboarding',
  '/custom-plan-form',
];

export const CookieBanner = () => {
  const [visible, setVisible] = useState(false);
  const location = useLocation();

  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) setVisible(true);
    } catch (e) {
      // Private browsing — show once but don't persist
      setVisible(true);
    }
  }, []);

  const accept = () => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ accepted: true, ts: new Date().toISOString() })
      );
    } catch (e) {
      // ignore
    }
    setVisible(false);
  };

  if (!visible) return null;

  // Auto-suppress on authenticated routes to avoid overlapping app controls
  // (admin Eye button, sidebar items, etc). Granting access to authenticated
  // routes implies prior acceptance of session cookies.
  const onAuthenticatedRoute = AUTHENTICATED_PATHS.some((p) =>
    location.pathname === p || location.pathname.startsWith(`${p}/`)
  );
  if (onAuthenticatedRoute) return null;

  return (
    <div
      className="fixed bottom-3 inset-x-3 sm:bottom-4 sm:right-auto sm:left-4 sm:max-w-md z-[200] rounded-2xl bg-zinc-950/95 backdrop-blur-md border border-[#ccff00]/20 shadow-2xl shadow-black/40 p-4 sm:p-5 animate-in fade-in slide-in-from-bottom-4 duration-300"
      role="dialog"
      aria-live="polite"
      data-testid="cookie-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl bg-[#ccff00]/15 flex items-center justify-center shrink-0">
          <Cookie className="w-5 h-5 text-[#ccff00]" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-white mb-1">Cookies técnicas</p>
          <p className="text-xs text-zinc-400 leading-relaxed">
            Usamos solo cookies estrictamente necesarias para que la app funcione
            (sesión, preferencias). <strong className="text-zinc-300">No tenemos cookies de marketing ni
            de seguimiento publicitario.</strong>{' '}
            <Link to="/cookies" className="text-[#ccff00] underline underline-offset-2">
              Más información
            </Link>
            .
          </p>
          <div className="flex items-center gap-2 mt-3">
            <Button
              onClick={accept}
              className="btn-primary !py-2 !px-4 !text-xs"
              data-testid="cookie-banner-accept"
            >
              Aceptar y cerrar
            </Button>
            <Link to="/privacy">
              <Button variant="ghost" className="!py-2 !px-3 !text-xs text-zinc-400 hover:text-white">
                Política de privacidad
              </Button>
            </Link>
          </div>
        </div>
        <button
          onClick={accept}
          aria-label="Cerrar"
          className="shrink-0 -mt-1 -mr-1 p-1 text-zinc-500 hover:text-white rounded transition"
          data-testid="cookie-banner-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default CookieBanner;
